import React, { useState } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { createZendeskTicket } from '../services/api'

// Region and Studio data matched to Zendesk tag files
const REGIONS = [
  'Arizona', 'Northern California', 'Southern California', 'Colorado', 'D.C. Metro', 'Florida',
  'Georgia', 'Illinois', 'Massachusetts', 'Minnesota', 'New Jersey', 'New York', 'North Carolina',
  'Oregon', 'Pennsylvania', 'South Carolina', 'Tennessee', 'Texas', 'Utah', 'Washington State',
  'Barrys X', 'Canada', 'Germany', 'Italy', 'Spain', 'Sweden', 'Switzerland', 'UAE', 'United Kingdom'
]

const STUDIOS_BY_REGION: { [key: string]: string[] } = {
  'Arizona': ['Scottsdale'],
  'Northern California': ['Burlingame', 'Castro', 'FIDI', 'Marina', 'Palo Alto', 'Santana Row', 'Walnut Creek'],
  'Southern California': ['Brentwood', 'Long Beach', 'Newport Beach', 'Pasadena', 'San Diego', 'Santa Monica', 'South Bay', 'Studio City', 'Venice', 'West Hollywood'],
  'Colorado': ['Denver'],
  'D.C. Metro': ['Arlington', 'DuPont Circle', 'Navy Yard'],
  'Florida': ['Aventura', 'Boca Raton', 'Miami Beach', 'Midtown', 'Tampa'],
  'Georgia': ['Atlanta', 'Midtown Atlanta'],
  'Illinois': ['Lincoln Park', 'River North', 'Wicker Park'],
  'Massachusetts': ['Back Bay', 'Chestnut Hill', 'Downtown Crossing'],
  'Minnesota': ['Minneapolis'],
  'New Jersey': ['Hoboken'],
  'New York': ['Brooklyn Heights', 'Chelsea', 'East 64th', 'East 86th', 'Long Island City', 'Noho', 'Park Ave South', 'Roslyn', 'Scarsdale', 'Southampton', 'Tribeca', 'Upper West Side', 'Wainscott', 'Wall Street', 'Williamsburg'],
  'North Carolina': ['Charlotte'],
  'Oregon': ['Portland'],
  'Pennsylvania': ['Philadelphia'],
  'South Carolina': ['Charleston'],
  'Tennessee': ['Nashville'],
  'Texas': ['Austin', 'Dallas', 'Houston'],
  'Utah': ['Salt Lake'],
  'Washington State': ['Seattle'],
  'Barrys X': ['Virtual Classes'],
  'Canada': ['Calgary', 'Richmond', 'Vancouver', 'Yorkville'],
  'Germany': ['Berlin', 'Frankfurt'],
  'Italy': ['Senato'],
  'Spain': ['Madrid'],
  'Sweden': ['Gothenburg', 'Stockholm'],
  'Switzerland': ['Zurich'],
  'UAE': ['DIFC', 'Dubai Marina'],
  'United Kingdom': ['Canary Wharf', 'Liverpool', 'London Central', 'London East', 'London SW1', 'London West', 'Manchester', 'Soho', 'St. Paul\'s']
}

// Zendesk region tag mappings (from ticket-fields-region.csv)
const REGION_TAGS: { [key: string]: string } = {
  'Arizona': 'arizona',
  'Northern California': 'northern_california',
  'Southern California': 'southern_california',
  'Colorado': 'colorado',
  'D.C. Metro': 'washington_dc_metro_area',
  'Florida': 'florida',
  'Georgia': 'georgia',
  'Illinois': 'illinois',
  'Massachusetts': 'massachusetts',
  'Minnesota': 'minnesota',
  'New Jersey': 'new_jersey',
  'New York': 'new_york',
  'North Carolina': 'north_carolina',
  'Oregon': 'oregon',
  'Pennsylvania': 'pennsylvania',
  'South Carolina': 'south_carolina',
  'Tennessee': 'tennessee',
  'Texas': 'texas',
  'Utah': 'utah',
  'Washington State': 'washington_state',
  'Canada': 'canada',
  'Germany': 'germany',
  'Italy': 'italy',
  'Spain': 'spain',
  'Sweden': 'sweden',
  'Switzerland': 'switzerland',
  'UAE': 'uae',
  'United Kingdom': 'uk'
}

// Per-region location field IDs from Zendesk (ticket-fields.csv)
const LOCATION_FIELD_IDS: { [key: string]: number } = {
  'Arizona': 13347713529883,
  'Northern California': 1900009081724,
  'Southern California': 1260829420629,
  'Colorado': 1260829336789,
  'D.C. Metro': 1900009083324,
  'Florida': 7618689202971,
  'Georgia': 1900009002584,
  'Illinois': 1260829401529,
  'Massachusetts': 1260829419629,
  'Minnesota': 1260829555350,
  'New Jersey': 33801198423963,
  'New York': 1900009081204,
  'North Carolina': 1260829420449,
  'Oregon': 1260829556190,
  'Pennsylvania': 1900009081744,
  'South Carolina': 34115050460955,
  'Tennessee': 1900009081804,
  'Texas': 1260829420849,
  'Utah': 35357957544091,
  'Washington State': 1260829421809,
  'Barrys X': 1260829421969,
  'Canada': 4415439920923,
  'Germany': 40973939640731,
  'Italy': 40974023189275,
  'Spain': 40974055934235,
  'Sweden': 40974120534683,
  'Switzerland': 40974225252763,
  'UAE': 40974206319259
}

// Zendesk location tag mappings (from per-region location CSVs)
const LOCATION_TAGS: { [region: string]: { [studio: string]: string } } = {
  'Arizona': { 'Scottsdale': 'scottsdale' },
  'Northern California': { 'Burlingame': 'burlingame', 'Castro': 'castro', 'FIDI': 'fidi', 'Marina': 'marina', 'Palo Alto': 'palo_alto', 'Santana Row': 'santana_row', 'Walnut Creek': 'walnut_creek' },
  'Southern California': { 'Brentwood': 'brentwood', 'Long Beach': 'long_beach', 'Newport Beach': 'newport_beach', 'Pasadena': 'pasadena', 'San Diego': 'san_diego', 'Santa Monica': 'santa_monica', 'South Bay': 'south_bay', 'Studio City': 'studio_city', 'Venice': 'venice', 'West Hollywood': 'west_hollywood' },
  'Colorado': { 'Denver': 'denver' },
  'D.C. Metro': { 'Arlington': 'arlington', 'DuPont Circle': 'dupont_circle', 'Navy Yard': 'navy_yard' },
  'Florida': { 'Aventura': 'aventura', 'Boca Raton': 'boca_raton', 'Miami Beach': 'miami_beach', 'Midtown': 'midtown', 'Tampa': 'tampa' },
  'Georgia': { 'Atlanta': 'atlanta', 'Midtown Atlanta': 'midtown_atlanta' },
  'Illinois': { 'Lincoln Park': 'lincoln_park', 'River North': 'river_north', 'Wicker Park': 'wicker_park' },
  'Massachusetts': { 'Back Bay': 'back_bay', 'Chestnut Hill': 'chestnut_hill', 'Downtown Crossing': 'downtown_crossing' },
  'Minnesota': { 'Minneapolis': 'minneapolis' },
  'New Jersey': { 'Hoboken': 'hoboken' },
  'New York': { 'Brooklyn Heights': 'brooklyn_heights', 'Chelsea': 'chelsea', 'East 64th': 'east_64th', 'East 86th': 'east_86th', 'Long Island City': 'long_island_city', 'Noho': 'noho', 'Park Ave South': 'park_ave_south', 'Roslyn': 'roslyn', 'Scarsdale': 'scarsdale', 'Southampton': 'southhampton', 'Tribeca': 'tribeca', 'Upper West Side': 'upper_west_side', 'Wainscott': 'wainscott', 'Wall Street': 'wall_street', 'Williamsburg': 'williamsburg' },
  'North Carolina': { 'Charlotte': 'charlotte' },
  'Oregon': { 'Portland': 'portland' },
  'Pennsylvania': { 'Philadelphia': 'philadelphia' },
  'South Carolina': { 'Charleston': 'charleston' },
  'Tennessee': { 'Nashville': 'nashville' },
  'Texas': { 'Austin': 'austin', 'Dallas': 'dallas', 'Houston': 'houston' },
  'Utah': { 'Salt Lake': 'salt_lake' },
  'Washington State': { 'Seattle': 'seattle' },
  'Canada': { 'Calgary': 'calgary', 'Richmond': 'richmond', 'Vancouver': 'vancouver', 'Yorkville': 'yorkville' },
  'Germany': { 'Berlin': 'berlin', 'Frankfurt': 'frankfurt' },
  'Italy': { 'Senato': 'senato' },
  'Spain': { 'Madrid': 'madrid' },
  'Sweden': { 'Stockholm': 'stockholm', 'Gothenburg': 'gothenburg' },
  'Switzerland': { 'Zurich': 'zurich' },
  'UAE': { 'DIFC': 'difc', 'Dubai Marina': 'dubai_marina' }
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`

const ModalContent = styled.div`
  background: ${theme.color.background.light};
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-bottom: 1px solid ${theme.color.border.light};
  background: ${theme.color.background.frame};
  border-radius: 12px 12px 0 0;
`

const ModalTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2.4rem;
  color: ${theme.color.text.grey};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${theme.color.text.base};
  }
`

const Form = styled.form`
  padding: 2rem;
`

const FieldContainer = styled.div`
  margin-bottom: 2rem;
`

const Label = styled.label`
  display: block;
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 0.8rem;
`

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 1rem;
  font-size: 1.4rem;
  border: 1px solid ${props => props.hasError ? '#ff0000' : theme.color.border.light};
  border-radius: 4px;
  background: ${props => props.disabled ? '#f5f5f5' : 'white'};
  color: ${theme.color.text.base};

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff0000' : theme.color.accent.primary};
  }

  &::placeholder {
    color: ${theme.color.text.grey};
  }

  &:disabled {
    cursor: not-allowed;
  }
`

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 1rem;
  font-size: 1.4rem;
  border: 1px solid ${props => props.hasError ? '#ff0000' : theme.color.border.light};
  border-radius: 4px;
  background: white;
  color: ${theme.color.text.base};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff0000' : theme.color.accent.primary};
  }
`

const TextArea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  padding: 1rem;
  font-size: 1.4rem;
  border: 1px solid ${props => props.hasError ? '#ff0000' : theme.color.border.light};
  border-radius: 4px;
  background: white;
  color: ${theme.color.text.base};
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff0000' : theme.color.accent.primary};
  }

  &::placeholder {
    color: ${theme.color.text.grey};
  }
`

const ErrorText = styled.span`
  display: block;
  color: #ff0000;
  font-size: 1.2rem;
  margin-top: 0.5rem;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 1rem 2rem;
  font-size: 1.4rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' ? `
    background: ${theme.color.accent.primary};
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background: #b8002d;
    }

    &:disabled {
      background: ${theme.color.text.grey};
      cursor: not-allowed;
      opacity: 0.6;
    }
  ` : `
    background: transparent;
    color: ${theme.color.text.base};
    border: 1px solid ${theme.color.border.light};

    &:hover {
      background: ${theme.color.background.frame};
    }
  `}
`

const SuccessMessage = styled.div`
  background: #e8f5e9;
  color: #2e7d32;
  padding: 1.5rem;
  border-radius: 4px;
  margin-bottom: 2rem;
  font-size: 1.4rem;
`

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 1.5rem;
  border-radius: 4px;
  margin-bottom: 2rem;
  font-size: 1.4rem;
`

interface ChallengeHelpTicketModalProps {
  isOpen: boolean
  onClose: () => void
  userData: any
}

const ChallengeHelpTicketModal: React.FC<ChallengeHelpTicketModalProps> = ({
  isOpen,
  onClose,
  userData
}) => {
  const [formData, setFormData] = useState({
    region: '',
    studio: '',
    subtopic: '',
    description: ''
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  if (!isOpen) return null

  const userName = userData?.user?.first_name && userData?.user?.last_name
    ? `${userData.user.first_name} ${userData.user.last_name}`
    : ''
  const userEmail = userData?.user?.email || ''

  const updateField = (field: string, value: string) => {
    const newData = { ...formData, [field]: value }

    // Reset studio when region changes
    if (field === 'region') {
      newData.studio = ''
    }

    setFormData(newData)

    if (errors[field]) {
      const newErrors = { ...errors }
      delete newErrors[field]
      setErrors(newErrors)
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.region.trim()) {
      newErrors.region = 'Region is required'
    }
    if (!formData.studio.trim()) {
      newErrors.studio = 'Studio is required'
    }
    if (!formData.subtopic) {
      newErrors.subtopic = 'Subtopic is required'
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitError('')
    setSubmitSuccess(false)

    try {
      const subtopicLabels: { [key: string]: string } = {
        points_missing: 'Points Missing',
        class_count_incorrect: 'Class Count Incorrect',
        sign_up_issues: 'Sign Up Issues',
        other: 'Other'
      }

      const description = `Challenge Campaign Help Request

Region: ${formData.region}
Studio: ${formData.studio}
Issue Type: ${subtopicLabels[formData.subtopic]}

Description:
${formData.description}`

      const tags = ['challenge_campaign']
      const regionTag = REGION_TAGS[formData.region]
      if (regionTag) tags.push(regionTag)
      const locationTag = LOCATION_TAGS[formData.region]?.[formData.studio]
      if (locationTag) tags.push(locationTag)

      // Build custom fields - use region-specific location field ID
      const locationFieldId = LOCATION_FIELD_IDS[formData.region]
      const customFields = [
        {
          id: 1260827787830,
          value: 'challenge_campaign'
        },
        {
          id: 42944406016283,
          value: formData.subtopic
        },
        {
          id: 1900007222284,
          value: regionTag || ''
        },
        {
          id: 1260825900050,
          value: '1260813793450'
        },
        {
          id: 1260825900030,
          value: '1260813793450'
        }
      ]
      if (locationFieldId && locationTag) {
        customFields.push({ id: locationFieldId, value: locationTag })
      }

      await createZendeskTicket({
        subject: 'Challenge Campaign - Help Request',
        comment: description,
        requester_email: userEmail,
        requester_name: userName,
        tags,
        group_id: 1260813793450,
        custom_fields: customFields
      })

      setSubmitSuccess(true)
      setFormData({
        region: '',
        studio: '',
        subtopic: '',
        description: ''
      })

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose()
        setSubmitSuccess(false)
      }, 2000)

    } catch (error) {
      console.error('Failed to submit challenge help ticket:', error)
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit help ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
      // Reset form state after closing
      setTimeout(() => {
        setFormData({
          region: '',
          studio: '',
          subtopic: '',
          description: ''
        })
        setErrors({})
        setSubmitSuccess(false)
        setSubmitError('')
      }, 300)
    }
  }

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Challenge Help Ticket</ModalTitle>
          <CloseButton onClick={handleClose} disabled={isSubmitting}>×</CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          {submitSuccess && (
            <SuccessMessage>
              Challenge help ticket submitted successfully!
            </SuccessMessage>
          )}

          {submitError && (
            <ErrorMessage>{submitError}</ErrorMessage>
          )}

          <FieldContainer>
            <Label>Name</Label>
            <Input
              type="text"
              value={userName}
              disabled
            />
          </FieldContainer>

          <FieldContainer>
            <Label>Email</Label>
            <Input
              type="email"
              value={userEmail}
              disabled
            />
          </FieldContainer>

          <FieldContainer>
            <Label>Region *</Label>
            <Select
              value={formData.region}
              onChange={(e) => updateField('region', e.target.value)}
              hasError={!!errors.region}
              disabled={isSubmitting}
            >
              <option value="">Select a region</option>
              {REGIONS.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </Select>
            {errors.region && <ErrorText>{errors.region}</ErrorText>}
          </FieldContainer>

          {formData.region && STUDIOS_BY_REGION[formData.region] && (
            <FieldContainer>
              <Label>Studio *</Label>
              <Select
                value={formData.studio}
                onChange={(e) => updateField('studio', e.target.value)}
                hasError={!!errors.studio}
                disabled={isSubmitting}
              >
                <option value="">Select a studio</option>
                {STUDIOS_BY_REGION[formData.region].map(studio => (
                  <option key={studio} value={studio}>{studio}</option>
                ))}
              </Select>
              {errors.studio && <ErrorText>{errors.studio}</ErrorText>}
            </FieldContainer>
          )}

          <FieldContainer>
            <Label>Subtopic *</Label>
            <Select
              value={formData.subtopic}
              onChange={(e) => updateField('subtopic', e.target.value)}
              hasError={!!errors.subtopic}
              disabled={isSubmitting}
            >
              <option value="">Select an issue type</option>
              <option value="points_missing">Points Missing</option>
              <option value="class_count_incorrect">Class Count Incorrect</option>
              <option value="sign_up_issues">Sign Up Issues</option>
              <option value="other">Other</option>
            </Select>
            {errors.subtopic && <ErrorText>{errors.subtopic}</ErrorText>}
          </FieldContainer>

          <FieldContainer>
            <Label>Description *</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Please describe the issue in detail..."
              hasError={!!errors.description}
              disabled={isSubmitting}
            />
            {errors.description && <ErrorText>{errors.description}</ErrorText>}
          </FieldContainer>

          <ButtonContainer>
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
            </Button>
          </ButtonContainer>
        </Form>
      </ModalContent>
    </ModalOverlay>
  )
}

export default ChallengeHelpTicketModal
